/* eslint-disable react/no-array-index-key */
/* eslint-disable indent */
import React, { useContext, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { cloneDeep, flatten } from 'lodash-es';
import { useTheme } from '@material-ui/core/styles';
import {
  MummsPDFGlobalContext,
  SET_PDF_MEASURES_GRAPH_IMAGES,
} from '@mummssoftware/mumms-print';
import { svgToImageURL } from '@mummssoftware/utils/pdf-print-graph/utils';
import { IDGGlobalContext } from '@mummssoftware/idg';
import {
  MeasuresPrintDiv,
  PrintTextDiv,
  NamesWrapper,
  ChartWrapper,
  ScoreName,
  SparklineDiv,
  MeasureNameDiv,
  MeasuresWrapper,
  PageBreak,
} from '../../components/layout/WrapperDiv';
import {
  getMeasuresForGraph,
  getTypesByName,
  getDateRange,
  orderVitalsMeasureNames,
} from '../MeasuresContext/utils/utils';
import {
  highChartTimelineHeight,
  sortedMeasureEntries,
} from '../MeasuresContext/utils/constants';
import {
  chartOptions,
  fastCategory,
  nyhaCategory,
} from '../Measures/Graph/chart-data';
import { AllPrintMeasuresDefined } from '../MeasuresContext/utils/type';

require('highcharts/modules/exporting')(Highcharts);
// offline-exporting module NECESSARY to avoid any sort of Highcharts server calls
require('highcharts/modules/offline-exporting')(Highcharts);

export declare const RouteProps: import('react-router-dom').RouteComponentProps<{
  patientNumber: string;
}>;

type MeasuresGraphPrintProps = {
  allMeasures: AllPrintMeasuresDefined;
};

export const MeasuresGraphPrint = React.memo(
  ({ allMeasures }: MeasuresGraphPrintProps) => {
    const chartHeight = 90;
    const { measures, allMeasuresTypes } = allMeasures;
    // black color for printing, issue with the graph not allowing print color
    const printColor = null;

    const {
      dispatchReactPDF,
      pdfMeasuresGraphImages,
      jsonMeasures,
    } = useContext(MummsPDFGlobalContext);

    const { documentData } = useContext(IDGGlobalContext);

    const [hasData, setHasData] = useState(false);

    useEffect(() => {
      if (
        Highcharts.charts &&
        jsonMeasures &&
        !pdfMeasuresGraphImages &&
        !hasData
      ) {
        const chartsAsBase64Promises = Highcharts.charts
          .filter((chart: any) => chart && chart.userOptions.title)
          .map((printChart: any, i: number, printChartArray: any) => {
            /* 
            if the previous chart in the list has a populated x-axis, that's
            a sign a new Measures page/section needs to be made; this logic checks for 
            that and sends the data to ReactPDF to conditionally render appropriately
            */
            const prevChartXAxis: any = printChartArray[i - 1]?.xAxis[0];

            const prevChartXAxisPopulated =
              prevChartXAxis && prevChartXAxis.axisGroup;

            const sectionTitle =
              (prevChartXAxisPopulated && printChart.userOptions.title) ||
              (!prevChartXAxis && printChart.userOptions.title);

            const svgString = printChart.getSVG({
              title: {
                text: (sectionTitle && sectionTitle[1].toUpperCase()) || null,
              },
              chart: {
                width: 700,
                height: 150,
                spacingBottom: 20,
                plotBorderWidth: 0.5,
              },
              plotOptions: {
                series: {
                  lineWidth: 2,
                },
              },
            });
            return svgToImageURL(svgString, 700, 150).then((res: any) => {
              const src = res;
              const singleGraphTitles = printChart.userOptions.title;
              /* 
              breakSection = break measures graph section so limitations, vitals etc
              graphs are on separate pages (refer to previous comment above)
               */
              return (
                (sectionTitle && {
                  breakSection: true,
                  src,
                  singleGraphTitles,
                }) || { singleGraphTitles, src }
              );
            });
          });

        if (chartsAsBase64Promises.length > 0) {
          Promise.all(chartsAsBase64Promises).then(
            (base64GraphConfigArray: mumms.MeasuresPDFGraphConfig[]) => {
              setHasData(true);
              dispatchReactPDF({
                type: SET_PDF_MEASURES_GRAPH_IMAGES,
                payload: base64GraphConfigArray,
              });
            },
          );
        } else if (chartsAsBase64Promises.length === 0) {
          setHasData(true);
          dispatchReactPDF({
            type: SET_PDF_MEASURES_GRAPH_IMAGES,
            payload: [],
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Highcharts.charts, pdfMeasuresGraphImages, jsonMeasures]);

    const getNamesUI = (chartmeasures: mumms.formattedAllMeasures) => {
      if (chartmeasures && Object.keys(chartmeasures).length) {
        return orderVitalsMeasureNames(Object.keys(chartmeasures)).map((name) =>
          getSingleNameUI(name),
        );
      }
      return null;
    };
    const getSingleNameUI = (name: string) => (
      <SparklineDiv
        key={name}
        height={chartHeight}
        className="measures-spark-line"
      >
        <MeasureNameDiv print>
          <ScoreName>{name.toUpperCase()}</ScoreName>
        </MeasureNameDiv>
      </SparklineDiv>
    );
    const getSingleChartUI = (
      nameType: mumms.measureTypeNames,
      chartdata: mumms.formattedAllMeasures,
      chartmeasures: mumms.measure[],
      name: mumms.measureShortNames,
      index: number,
    ) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const theme = useTheme();
      const numberMeasures = Object.keys(chartdata).length;
      const measuresdate: any = Object.values(chartdata);
      const allMeasuresFordate: any = flatten(measuresdate);
      const dateRange = getDateRange(allMeasuresFordate);
      const typeNames: Record<
        mumms.measureShortNames,
        mumms.measureType
      > = getTypesByName((allMeasuresTypes as any)[nameType]);

      const show2ndData = typeNames[name]?.isgraphwithsecondaryval === 'true';
      const options = cloneDeep(
        chartOptions(printColor, [name, nameType], !!pdfMeasuresGraphImages),
      );
      options.chart.height = chartHeight;
      options.chart.marginLeft = 60;
      (options.yAxis as any).visible = true;

      let measureValues: any;

      // FAST / NYHA category axes
      if (name === 'fast') {
        (options.yAxis as any).categories = fastCategory;
        measureValues = getMeasuresForGraph(
          chartmeasures,
          dateRange,
          'value',
          'fast',
        );
      } else if (name === 'nyha') {
        (options.yAxis as any).categories = nyhaCategory;
        measureValues = getMeasuresForGraph(
          chartmeasures,
          dateRange,
          'value',
          'nyha',
        );
      } else {
        measureValues = getMeasuresForGraph(chartmeasures, dateRange, 'value');
      }

      (options.xAxis as any).plotBands = null;

      /** only display last timeline */
      // TODO not working
      if (
        index === numberMeasures - 1 &&
        measureValues &&
        measureValues.length
      ) {
        (options.xAxis as any).visible = true;
        options.chart.height = chartHeight + highChartTimelineHeight;
      }

      const firstSerie = {
        id: name,
        data: measureValues,
        name: '',
        type: 'line',
        color: (theme.palette as any).graph.graphColor,
        fillOpacity: 0.3,
      };
      options.series = [firstSerie];

      if (show2ndData) {
        // backend value2 to graph 2nd value
        const measureValues2 = getMeasuresForGraph(
          chartmeasures,
          dateRange,
          'value2',
        );
        const secondSerie = {
          id: 'other',
          data: measureValues2,
          name: '',
          type: 'line',
          color: (theme.palette as any).graph.secondGraphColor,
          fillOpacity: 0.3,
        };
        options.series = [firstSerie, secondSerie];
      }
      // had put a key in hoping it would trigger a HighChart re-render
      return (
        <HighchartsReact
          key={index}
          highcharts={Highcharts}
          options={options as any}
        />
      );
    };
    const getChartUI = (
      _nameType: mumms.measureTypeNames,
      chartmeasures: mumms.formattedAllMeasures,
    ) => {
      if (chartmeasures && Object.keys(chartmeasures).length) {
        return orderVitalsMeasureNames(
          Object.keys(chartmeasures),
        ).map((name, index) =>
          getSingleChartUI(
            _nameType,
            chartmeasures,
            (chartmeasures as any)[name],
            name,
            index,
          ),
        );
      }
      return null;
    };
    const getGraphsByCategory = (
      _name: mumms.measureTypeNames,
      _measures: mumms.formattedAllMeasures,
    ) => (
      <MeasuresWrapper id="graph-area" className="measures-graph-area" print>
        {!documentData && (
          <NamesWrapper print id="names" className="measures-name-wrapper">
            {getNamesUI(_measures as any)}
          </NamesWrapper>
        )}
        <ChartWrapper>{getChartUI(_name, _measures)}</ChartWrapper>
      </MeasuresWrapper>
    );

    const getGraphsPrint = (_measures: mumms.measureByType) => {
      const sortedEntries = sortedMeasureEntries(_measures, allMeasuresTypes);
      return sortedEntries.map(([typename, typevalues], i) => (
        <React.Fragment key={typename}>
          <MeasuresPrintDiv>
            <PrintTextDiv key={`${typename}`} className="measure-print-text">
              {typename}
            </PrintTextDiv>
            {getGraphsByCategory(
              typename as mumms.measureTypeNames,
              typevalues as mumms.formattedAllMeasures,
            )}
          </MeasuresPrintDiv>
          {i === sortedEntries.length - 1 ? null : <PageBreak />}
        </React.Fragment>
      ));
    };

    const getMeasuresGraphPrint = (_measures: mumms.measureByType) => (
      <MeasuresPrintDiv>{getGraphsPrint(_measures)}</MeasuresPrintDiv>
    );

    return <React.Fragment>{getMeasuresGraphPrint(measures)}</React.Fragment>;
  },
);

export default MeasuresGraphPrint;
